/**
 * `diffNoteAnchors` — the PURE product-side producer that lowers elaborate's
 * existing comment reads into the `@norkron/web-review-workbench`
 * {@link DiffNoteAnchor}[] the `<SideBySideDiff>` renders as clickable note badges
 * on the hunk whose `changeHash` matches (design §3–§4, frontend render seam).
 *
 * A diff note is an ORDINARY comment (same threading / visibility / rich body) whose
 * anchor happens to pin a diff change (`change_hash`) instead of a doc span — so
 * "persistence via existing comment reads" means: the SAME comment array the gutters
 * render, filtered to the rows that carry a diff-change anchor, grouped by their
 * thread root. This lib code never fetches; it maps what the comment read already
 * returned. When the backend has not yet stamped a `change_hash` on any comment the
 * result is simply `[]` — the seam is wired and lights up when the data arrives.
 *
 * RULE ZERO: the generic anchor RENDER lives in the lib; only the elaborate mapping
 * from a comment row to the presentational anchor lives here.
 */

import type { DiffNoteAnchor } from "@norkron/web-review-workbench";

/**
 * The minimal comment shape this mapper reads — a structural subset of the app's
 * `Comment` (id + threading), plus the OPTIONAL diff-anchor id a diff note carries.
 * Kept structural (not the full `Comment`) so it is trivially unit-testable and so
 * a legacy comment row without `change_hash` is simply skipped.
 */
export interface DiffAnchoredComment {
  readonly id: number;
  readonly parent_id: number | null;
  /** The content-addressed diff change this comment is anchored to (design §2.2).
   *  Present only on a diff note; absent on an ordinary doc-span comment. */
  readonly change_hash?: string | null;
}

/**
 * Resolve a comment id to its THREAD ROOT id within a loaded comment set (walking
 * `parent_id` up). A deep-link `?comment=<cid>&reply=<rid>` addresses a reply, but
 * the diff-note badge lives on the ROOT's change — so the reveal target is the root.
 * Returns the input id unchanged when it is already a root or is not found (safe).
 */
export function resolveThreadRoot(
  comments: readonly DiffAnchoredComment[],
  commentId: number,
): number {
  const byId = new Map<number, DiffAnchoredComment>();
  for (const c of comments) byId.set(c.id, c);
  let current = byId.get(commentId);
  // Bounded walk (guard against a malformed cycle) up to the root.
  for (let i = 0; i < comments.length + 1; i += 1) {
    if (current === undefined) return commentId;
    if (current.parent_id === null) return current.id;
    const next = byId.get(current.parent_id);
    if (next === undefined) return current.id;
    current = next;
  }
  return commentId;
}

/**
 * Group the diff-anchored comments into one {@link DiffNoteAnchor} per (thread root,
 * change) — the badge the `<SideBySideDiff>` draws on the matching hunk. The badge
 * `count` is the number of NON-resolved-away notes in that thread (root + replies)
 * that share the change, so a reviewer sees how many notes a change carries.
 *
 * Only THREAD ROOTS that themselves carry a `change_hash` produce a badge (a reply
 * inherits its root's change; its own row need not repeat the anchor). Replies that
 * carry the same `change_hash` bump their root's count.
 */
export function diffNoteAnchorsFromComments(
  comments: readonly DiffAnchoredComment[],
): DiffNoteAnchor[] {
  const rootChange = new Map<number, string>(); // root id → change hash
  const byId = new Map<number, DiffAnchoredComment>();
  for (const c of comments) byId.set(c.id, c);

  // 1. Roots that ARE diff notes.
  for (const c of comments) {
    if (c.parent_id === null && c.change_hash != null && c.change_hash !== "") {
      rootChange.set(c.id, c.change_hash);
    }
  }
  if (rootChange.size === 0) return [];

  // 2. Count each diff-anchored thread's size (root + every reply). A reply
  //    INHERITS its root's change anchor — it need not repeat the `change_hash` — so
  //    the badge count is the whole thread, not just the rows that echo the hash.
  const counts = new Map<number, number>();
  for (const c of comments) {
    const root = resolveThreadRoot(comments, c.id);
    if (rootChange.has(root)) counts.set(root, (counts.get(root) ?? 0) + 1);
  }

  const anchors: DiffNoteAnchor[] = [];
  for (const [rootId, changeHash] of rootChange) {
    anchors.push({
      changeHash,
      commentId: String(rootId),
      count: counts.get(rootId) ?? 1,
    });
  }
  return anchors;
}
